/**
 * Camada cartográfica vetorial (PMTiles / MVT) integrada ao Leaflet via `protomaps-leaflet`.
 *
 * Renderiza os vetores do OpenStreetMap em Canvas 2D diretamente no `L.GridLayer` do Leaflet,
 * aplicando as paletas clara e escura definidas no código (`mapConfig.js`) e atualizando o
 * tema dinamicamente sem recriar o mapa nem buscar novos dados na rede.
 *
 * @module components/VectorBasemapLayer
 */

import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { leafletLayer } from "protomaps-leaflet";
import {
  createVectorLabelRules,
  createVectorPaintRules,
  getVectorMapPalette,
} from "../constants/mapConfig";

/**
 * Resolve uma URL relativa (ex.: `/maps/artur-nogueira.pmtiles`) para URL absoluta do navegador.
 *
 * @param {string} rawUrl
 * @returns {string}
 */
function resolveRuntimeTileUrl(rawUrl) {
  if (!rawUrl) return "";
  if (rawUrl.startsWith("/") && typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${rawUrl}`;
  }
  return rawUrl;
}

/**
 * Componente React-Leaflet que gerencia o ciclo de vida da camada vetorial `protomaps-leaflet`.
 *
 * @param {{
 *   url: string,
 *   attribution: string,
 *   minZoom?: number,
 *   maxZoom?: number,
 *   maxDataZoom?: number,
 *   isDarkMode?: boolean,
 *   onTileLoad?: (event: { src: string }) => void,
 *   onTileError?: (error: Error) => void
 * }} props
 */
export default function VectorBasemapLayer({
  url,
  attribution,
  minZoom = 0,
  maxZoom = 19,
  maxDataZoom = 15,
  isDarkMode = false,
  onTileLoad,
  onTileError,
}) {
  const map = useMap();
  const layerRef = useRef(null);
  const initialDarkModeRef = useRef(isDarkMode);
  const callbacksRef = useRef({ onTileLoad, onTileError });

  useEffect(() => {
    callbacksRef.current = { onTileLoad, onTileError };
  }, [onTileLoad, onTileError]);

  // 1. Montagem e desmontagem da camada vetorial no mapa Leaflet
  useEffect(() => {
    if (!map || !url) return undefined;

    const dark = initialDarkModeRef.current;
    const palette = getVectorMapPalette(dark);
    const resolvedUrl = resolveRuntimeTileUrl(url);

    const layer = leafletLayer({
      url: resolvedUrl,
      attribution,
      minZoom,
      maxZoom,
      maxDataZoom,
      lang: "pt",
      backgroundColor: palette.background,
      paintRules: createVectorPaintRules(dark),
      labelRules: createVectorLabelRules(dark, "pt"),
      pane: "tilePane",
    });

    // Instrumenta as views da camada vetorial para reportar sucesso ou falha de carregamento
    if (layer.views && typeof layer.views.values === "function") {
      for (const view of layer.views.values()) {
        if (typeof view.getDisplayTile === "function") {
          const origGetDisplayTile = view.getDisplayTile.bind(view);
          view.getDisplayTile = async (displayTile) => {
            try {
              const prepared = await origGetDisplayTile(displayTile);
              callbacksRef.current.onTileLoad?.({ src: resolvedUrl });
              return prepared;
            } catch (err) {
              if (err?.name !== "AbortError") {
                callbacksRef.current.onTileError?.(err);
              }
              throw err;
            }
          };
        }
      }
    }

    layerRef.current = layer;
    layer.addTo(map);

    return () => {
      layerRef.current = null;
      try {
        map.removeLayer(layer);
      } catch {
        // Ignora erro se o mapa já foi desmontado
      }
    };
  }, [map, url, attribution, minZoom, maxZoom, maxDataZoom]);

  // 2. Atualização instantânea das cores ao alternar entre Modo Claro e Modo Escuro
  useEffect(() => {
    initialDarkModeRef.current = isDarkMode;
    const layer = layerRef.current;
    if (!layer) return;

    const palette = getVectorMapPalette(isDarkMode);
    layer.backgroundColor = palette.background;
    layer.paintRules = createVectorPaintRules(isDarkMode);
    layer.labelRules = createVectorLabelRules(isDarkMode, "pt");

    if (typeof layer.clearLayout === "function") {
      layer.clearLayout();
    }
    if (typeof layer.rerenderTiles === "function") {
      layer.rerenderTiles();
    }
  }, [isDarkMode]);

  return null;
}
